import React, { createContext, useContext, useEffect, useState } from 'react';
import { ServerInfo } from '@/types/globals';

interface ServerInfoContextType {
  serverInfo: ServerInfo | null;
  isLoading: boolean;
}

const ServerInfoContext = createContext<ServerInfoContextType | undefined>(undefined);

interface ServerInfoProviderProps {
  children: React.ReactNode;
  /** Skips the fetch. React islands pass the SERVER_INFO global their Knockout host already has. */
  initialServerInfo?: ServerInfo | null;
}

export function ServerInfoProvider({ children, initialServerInfo }: ServerInfoProviderProps) {
  const [serverInfo, setServerInfo] = useState<ServerInfo | null>(initialServerInfo ?? null);
  const [isLoading, setIsLoading] = useState(!initialServerInfo);

  useEffect(() => {
    if (initialServerInfo) {
      return;
    }

    const loadServerInfo = async () => {
      try {
        setIsLoading(true);

        const response = await fetch('/api/server_info.js');
        if (!response.ok) {
          throw new Error(`Failed to fetch server info: ${response.status}`);
        }

        const scriptContent = await response.text();

        // Extract SERVER_INFO from the JavaScript response
        const match = scriptContent.match(/var SERVER_INFO = ({.*});/);
        if (match) {
          const serverInfoData = JSON.parse(match[1]);
          setServerInfo(serverInfoData);
        } else {
          throw new Error('Could not parse SERVER_INFO from response');
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadServerInfo();
  }, [initialServerInfo]);

  return (
    <ServerInfoContext.Provider value={{ serverInfo, isLoading }}>
      {children}
    </ServerInfoContext.Provider>
  );
}

export function useServerInfoContext() {
  const context = useContext(ServerInfoContext);
  if (context === undefined) {
    throw new Error('useServerInfoContext must be used within a ServerInfoProvider');
  }
  return context;
}
